import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Clock, Users, BadgeCheck, CalendarClock, Wallet, ArrowLeft, CheckCircle2, Loader2, FileText, GraduationCap, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ShareDialog } from "@/components/ShareDialog";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ qualification: "", phone: "", notes: "" });
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => setCourse(false));
  }, [id]);

  const startEnroll = () => {
    if (!user || user === false) { toast.error("Please sign in to enroll"); nav("/login"); return; }
    if (user.role !== "professional") { toast.error("Only healthcare professionals can enroll"); return; }
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: appl } = await api.post("/applications", { course_id: id, ...form });
      toast.success("Application submitted — redirecting to payment");
      const { data: co } = await api.post("/payments/checkout", { application_id: appl.id, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to enroll");
      setSubmitting(false);
    }
  };

  if (course === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (course === false) return <div><Navbar /><div className="mx-auto max-w-3xl p-16 text-center">Course not found. <Link className="text-primary" to="/courses">Back to courses</Link></div></div>;

  const meta = [
    { icon: MapPin, label: "Location", value: `${course.city}, ${course.state}` },
    { icon: Clock, label: "Duration", value: course.duration },
    { icon: Users, label: "Seats", value: `${course.seats} available` },
    { icon: Wallet, label: "Stipend", value: course.stipend || "—" },
  ];

  return (
    <div>
      <Navbar />
      {/* Hero */}
      <div className="relative h-72 overflow-hidden">
        <img src={course.image_url} alt={course.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          <Button variant="secondary" size="sm" className="mb-4" onClick={() => nav(-1)} data-testid="back-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground">{course.specialty}</span>
          <h1 className="mt-3 font-heading text-3xl font-extrabold text-white sm:text-4xl">{course.title}</h1>
          {course.institute && (
            <div className="mt-2 flex items-center gap-2 text-white/90">
              {course.institute.name} {course.institute.verified && <BadgeCheck className="h-4 w-4 text-sky-300" />}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_360px] lg:px-8">
        <div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {meta.map((m) => (
              <div key={m.label} className="rounded-xl border border-border bg-card p-4">
                <m.icon className="h-5 w-5 text-primary" />
                <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">{m.label}</p>
                <p className="font-semibold">{m.value}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="overview" className="mt-8">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="curriculum" data-testid="tab-curriculum">Curriculum</TabsTrigger>
              <TabsTrigger value="eligibility" data-testid="tab-eligibility">Eligibility & Docs</TabsTrigger>
              <TabsTrigger value="admission" data-testid="tab-admission">Admission Process</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              <p className="leading-relaxed text-muted-foreground">{course.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(course.tags || []).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
              </div>
            </TabsContent>
            <TabsContent value="curriculum" className="pt-4">
              <ul className="space-y-3">
                {(course.curriculum || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" /><span>{c}</span></li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="eligibility" className="pt-4">
              <h4 className="flex items-center gap-2 font-semibold"><GraduationCap className="h-4 w-4" /> Who can apply</h4>
              <div className="mt-2 flex flex-wrap gap-2">{(course.eligibility || []).map((e) => <Badge key={e}>{e}</Badge>)}</div>
              <h4 className="mt-6 flex items-center gap-2 font-semibold"><FileText className="h-4 w-4" /> Documents required</h4>
              <ul className="mt-2 space-y-2">
                {(course.documents_required || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" /><span>{d}</span></li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="admission" className="pt-4">
              <ol className="space-y-4">
                {(course.admission_process || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary font-mono-nums text-sm font-bold text-primary-foreground">{i + 1}</span>
                    <span className="pt-1">{s}</span>
                  </li>
                ))}
              </ol>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sticky apply card */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <p className="text-sm text-muted-foreground">Course fee</p>
            <p className="font-mono-nums text-3xl font-extrabold text-foreground">{inr(course.fee)}</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Application fee</span><span className="font-mono-nums font-semibold">{inr(course.application_fee)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Format</span><span className="font-semibold">{course.format}</span></div>
              {course.application_deadline && (
                <div className="flex justify-between"><span className="text-muted-foreground">Apply by</span><span className="flex items-center gap-1 font-semibold"><CalendarClock className="h-3.5 w-3.5" />{course.application_deadline}</span></div>
              )}
            </div>
            <Button data-testid="enroll-btn" className="mt-6 w-full" size="lg" onClick={startEnroll}>Enroll Now</Button>
            <Button variant="outline" data-testid="share-btn" className="mt-3 w-full" onClick={() => setShareOpen(true)}><Share2 className="mr-2 h-4 w-4" /> Share course</Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Pay the application fee online to submit your application.</p>
          </div>
        </aside>
      </div>

      {/* Enroll wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle>Apply to {course.title}</DialogTitle>
            <DialogDescription>Confirm your details. You'll pay the {inr(course.application_fee)} application fee to submit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="qual">Highest qualification</Label>
              <Input id="qual" data-testid="enroll-qualification" value={form.qualification}
                onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="e.g. MBBS, B.Sc Nursing" />
            </div>
            <div>
              <Label htmlFor="phone">Contact number</Label>
              <Input id="phone" data-testid="enroll-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" />
            </div>
            <div>
              <Label htmlFor="notes">Note to institute (optional)</Label>
              <Textarea id="notes" data-testid="enroll-notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Why are you interested in this program?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button data-testid="enroll-pay-btn" onClick={submit} disabled={submitting}>
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing</> : <>Pay {inr(course.application_fee)} & Apply</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareDialog course={course} open={shareOpen} onOpenChange={setShareOpen} />

      <Footer />
    </div>
  );
}
